import { apiRequest } from './apiClient';

const DOTSTORMING_ACCESS_TOKEN_KEY = 'dotstorming_access_token';
const DOTSTORMING_REFRESH_TOKEN_KEY = 'dotstorming_refresh_token';
const DOTSTORMING_TOPIC_USER_PREFIX = 'dotstorming_topic_user_';
const TEAMS_HOSTNAME = 'teams.dotstorming.com';

type RefreshTokenResponse = {
  token?: {
    accessToken?: string;
    refreshToken?: string;
  };
};

const readLocalStorageValue = (key: string) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = window.localStorage.getItem(key);
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
};

const writeLocalStorageValue = (key: string, value: string | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!value) {
    window.localStorage.removeItem(key);
    return;
  }

  window.localStorage.setItem(key, value);
};

export const isTeamsHost = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.location.hostname.toLowerCase() === TEAMS_HOSTNAME;
};

const refreshWebAccessToken = async (refreshToken: string) => {
  try {
    const payload = await apiRequest<RefreshTokenResponse>('/users/refreshToken', {
      method: 'POST',
      body: JSON.stringify({ token: refreshToken }),
    });

    const refreshedAccessToken = payload?.token?.accessToken?.trim() ?? null;
    const refreshedRefreshToken = payload?.token?.refreshToken?.trim() ?? null;

    writeLocalStorageValue(DOTSTORMING_ACCESS_TOKEN_KEY, refreshedAccessToken);
    writeLocalStorageValue(DOTSTORMING_REFRESH_TOKEN_KEY, refreshedRefreshToken ?? refreshToken);

    return refreshedAccessToken;
  } catch {
    return null;
  }
};

export const resolveDotstormingWebAuthToken = async () => {
  const accessToken = readLocalStorageValue(DOTSTORMING_ACCESS_TOKEN_KEY);
  if (accessToken) {
    return accessToken;
  }

  const refreshToken = readLocalStorageValue(DOTSTORMING_REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return null;
  }

  return refreshWebAccessToken(refreshToken);
};

export const clearDotstormingWebAuthStorage = () => {
  if (typeof window === 'undefined') {
    return;
  }

  writeLocalStorageValue(DOTSTORMING_ACCESS_TOKEN_KEY, null);
  writeLocalStorageValue(DOTSTORMING_REFRESH_TOKEN_KEY, null);

  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(DOTSTORMING_TOPIC_USER_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      window.localStorage.removeItem(key);
    });
  } catch {
    // Ignore storage errors and keep logout flow moving.
  }
};
